import { Fragment } from "react";
import { Link } from "react-router";

import { Column, Row, Text } from "@/components";
import { Skeleton } from "@ui/skeleton";

import { leverPerformanceTierTextClass, screenTimeTier } from "@/lib/constants";
import { dashboardStafferStats } from "@/lib/dashboardStafferStats";
import { cn } from "@/lib/utils";
import { DashboardStafferScreenBreakdownRow, StafferStatKind } from "@/types";

const SKELETON_ROW_COUNT = 3;
// A lone screen is its own median, so the rule needs at least two rows.
const MIN_ROWS_FOR_MEDIAN_RULE = 2;

// Splits the slowest-first list where the week's median falls: everything above
// it took longer, everything below was faster.
const MedianRule = ({ medianHours }: { medianHours: number | null }) => (
  <Row className="shrink-0 items-center gap-2.5">
    <Column className="h-px flex-1 bg-lever-surface-neutral" />
    <Text className="shrink-0 font-plex-mono text-[9px] font-medium uppercase tracking-wider text-lever-text-label">
      Median{" "}
      {dashboardStafferStats.formatValue(
        StafferStatKind.TIME_TO_SCREEN,
        medianHours,
      )}
    </Text>
    <Column className="h-px flex-1 bg-lever-surface-neutral" />
  </Row>
);

// The candidacies behind the median, slowest first, split by the median rule.
export const ScreenBreakdownRows = ({
  isPending,
  medianHours,
  rows,
}: {
  isPending: boolean;
  medianHours: number | null;
  rows: DashboardStafferScreenBreakdownRow[];
}) => {
  /* Helpers */
  // guac returns the breakdown slowest-first, so the rule belongs after the
  // last row above the median.
  const medianRuleIndex =
    medianHours === null || rows.length < MIN_ROWS_FOR_MEDIAN_RULE
      ? null
      : rows.filter((row) => row.hoursToFirstScreen > medianHours).length;

  /* Render */
  const render = () => {
    if (isPending) {
      return (
        <Column className="flex-1 justify-center gap-3">
          {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
            <Skeleton key={index} className="h-6 w-full" />
          ))}
        </Column>
      );
    }

    if (rows.length === 0) {
      return (
        <Column className="flex-1 items-center justify-center">
          <Text className="text-xs font-normal italic text-lever-text-label">
            No screens in this period
          </Text>
        </Column>
      );
    }

    return (
      <Column className="no-scrollbar min-h-0 flex-1 gap-2.5 overflow-y-auto pt-2">
        {rows.map((row, index) => (
          <Fragment key={row.candidacyId}>
            {index === medianRuleIndex ? (
              <MedianRule medianHours={medianHours} />
            ) : null}
            <Link
              to={`/companies/${row.companyId}/jobs/${row.jobId}?c=${row.candidacyId}`}
              className="shrink-0"
            >
              <Row className="w-full items-center justify-between gap-4">
                <Column className="min-w-0 gap-0.5">
                  <Text className="truncate text-xs font-semibold leading-none text-lever-text-heading">
                    {row.candidateName}
                  </Text>
                  <Text className="truncate text-[11px] font-normal leading-none text-lever-text-label">
                    {row.companyName} • {row.jobTitle}
                  </Text>
                </Column>
                <Text
                  className={cn(
                    "shrink-0 text-xs font-semibold tabular-nums leading-none",
                    leverPerformanceTierTextClass[
                      screenTimeTier(row.hoursToFirstScreen)
                    ],
                  )}
                >
                  {row.hoursToFirstScreen.toString()}h
                </Text>
              </Row>
            </Link>
          </Fragment>
        ))}
      </Column>
    );
  };

  return render();
};
